import { useRef, useState } from 'react'

import { type SemanticIconName } from '~components/internal/Icon'
import { SECURE_CLOUDFRONT_DOMAIN } from '~config'
import { type Attachment, s3securebase, s3sha256base } from '~types'

const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg']

const fileTypeLabels: Record<string, string> = {
  docx: 'Word Document (DOCX)',
  doc: 'Word Document (DOC)',
  pdf: 'PDF Document',
  xlsx: 'Excel Spreadsheet (XLSX)',
  pptx: 'PowerPoint Presentation (PPTX)',
  csv: 'Spreadsheet (CSV)',
}

export class FileFetchError extends Error {
  constructor(readonly status: number) {
    super(`File request failed (${status})`)
  }
}

// Signed URLs carry their own auth, so this is a plain cross-origin GET with no cookies.
// no-store: S3 omits `Vary: Origin` on non-CORS responses, so an earlier <img> load of the
// same URL would otherwise be reused from cache without CORS headers and fail.
export async function fetchFileBlob(url: string): Promise<Blob> {
  const response = await fetch(url, { cache: 'no-store' })
  if (!response.ok) throw new FileFetchError(response.status)
  return response.blob()
}

export function getFileExtension(source: string): string {
  return source.split('?')[0].split('.').pop()?.toLowerCase() ?? ''
}

export function getFileTypeLabel(source: string): string {
  const extension = getFileExtension(source)
  if (fileTypeLabels[extension]) return fileTypeLabels[extension]
  return extension ? `${extension.toUpperCase()} File` : 'File'
}

export function getFileTypeIconName(source: string): SemanticIconName {
  const extension = getFileExtension(source)
  if (extension === 'pdf') return 'pictureAsPdf'
  if (imageExtensions.includes(extension)) return 'image'
  return 'filePresent'
}

export function isCaseExportArtifactUrl(url: string): boolean {
  return url.split('?')[0].includes('/case_file_zip/')
}

export function getFileViewUrl(url: string): string {
  if (
    url.startsWith('http') ||
    url.startsWith(s3securebase) ||
    url.startsWith(SECURE_CLOUDFRONT_DOMAIN)
  ) {
    return url
  }
  return url.startsWith(s3sha256base) ? url : `${s3sha256base}${url}`
}

/**
 * Custom hook for handling drag-and-drop file upload functionality
 * @param onDropCallback - Callback function to handle dropped files (can be passed as ref or direct function)
 * @param disabled - Whether drag-and-drop is disabled
 * @returns Object containing isDragging state and event handlers
 */
export function useDragAndDrop(
  onDropCallback: ((files: FileList) => void) | React.MutableRefObject<(files: FileList) => void>,
  disabled = false,
): {
  isDragging: boolean
  dragCounter: React.MutableRefObject<number>
  handleDragEnter: (e: React.DragEvent) => void
  handleDragLeave: (e: React.DragEvent) => void
  handleDragOver: (e: React.DragEvent) => void
  handleDrop: (e: React.DragEvent) => void
} {
  const [isDragging, setIsDragging] = useState(false)
  const dragCounter = useRef(0)

  const handleDragEnter = (e: React.DragEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      dragCounter.current++
      if (dragCounter.current === 1) {
        setIsDragging(true)
      }
    }
  }

  const handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setIsDragging(false)
    }
  }

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current = 0
    setIsDragging(false)

    if (disabled || !e.dataTransfer.files || e.dataTransfer.files.length === 0) return

    // Handle both direct function and ref
    const onDrop = typeof onDropCallback === 'function' ? onDropCallback : onDropCallback.current
    onDrop(e.dataTransfer.files)
  }

  return {
    isDragging,
    dragCounter,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
  }
}

export function downloadCSVData(headers: string[], data: string[][], filename: string): void {
  const csvContent = [
    ...(headers.length ? [headers.join(',')] : []),
    ...data.map((row) => row.join(',')),
  ].join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function downloadTXTData(data: string, filename: string): void {
  const blob = new Blob([data], { type: 'text/plain;charset=utf-8' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', `${filename}.txt`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function getFileNameFromS3Url(url: string): string {
  const segments = url.replace(/"/g, '').split('?')[0].split('/')
  return segments[segments.length - 1]
}

export function convertURLToAttachment(url: string, accessUrl?: string): Attachment {
  return {
    name: getFileNameFromS3Url(url),
    url: url,
    access_url: accessUrl,
  }
}
